/**
 * DPoP proof assembly (RFC 9449). A proof is a compact JWS the app signs with
 * a key the device holds but cannot export, and sends alongside a token so the
 * provider can tell that whoever presents the token also controls the key.
 *
 * Everything here is the *pure* half of that job — JWS serialisation, the RFC
 * 7638 thumbprint, base64url — and none of it does any crypto itself. The
 * random bytes, the SHA-256 digest and the signature all arrive as parameters,
 * because the two platforms have nothing in common underneath: web signs with
 * a non-extractable WebCrypto key and digests with `crypto.subtle`, while
 * mobile signs with a Keystore / Secure Enclave key through a native module
 * (which returns a DER signature — see `derToRawEcdsaSignature`) and digests
 * with crypto-js (`sha256Bytes` in ./utilities), React Native having no
 * WebCrypto.
 *
 * `ES256` is the algorithm because P-256 ECDSA is the one alg the provider
 * advertises that both platforms' key stores implement.
 *
 * The module has zero imports and touches no globals at import time — no
 * `btoa`, no `Buffer`, and `TextEncoder` is looked up per call, because on
 * Hermes the app installs the `TextEncoder` polyfill after its imports have
 * already evaluated.
 */

const BASE64_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const BASE64URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

const buildLookup = (alphabet) => {
  const lookup = {};
  for (let i = 0; i < alphabet.length; i += 1) {
    lookup[alphabet[i]] = i;
  }
  return lookup;
};

const BASE64_LOOKUP = buildLookup(BASE64_ALPHABET);
const BASE64URL_LOOKUP = buildLookup(BASE64URL_ALPHABET);

const toBytes = (input) => {
  if (input instanceof Uint8Array) return input;
  if (ArrayBuffer.isView(input)) {
    return new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
  }
  // `instanceof ArrayBuffer` is not enough: WebCrypto hands back a buffer from
  // its own realm (node's, under jest's jsdom) which fails the check, so an
  // ArrayBuffer is recognised by shape instead.
  if (input && typeof input.byteLength === 'number') {
    return new Uint8Array(input);
  }
  throw new Error('Expected a Uint8Array or an ArrayBuffer');
};

const encode = (input, alphabet, padded) => {
  const bytes = toBytes(input);
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];

    out += alphabet[b0 >> 2];
    out += alphabet[((b0 & 0x03) << 4) | (b1 === undefined ? 0 : b1 >> 4)];
    if (b1 === undefined) {
      out += padded ? '==' : '';
      break;
    }
    out += alphabet[((b1 & 0x0f) << 2) | (b2 === undefined ? 0 : b2 >> 6)];
    if (b2 === undefined) {
      out += padded ? '=' : '';
      break;
    }
    out += alphabet[b2 & 0x3f];
  }
  return out;
};

const decode = (value, lookup) => {
  const input = String(value).replace(/=+$/, '');
  const bytes = new Uint8Array(Math.floor((input.length * 3) / 4));
  let buffer = 0;
  let bits = 0;
  let index = 0;

  for (let i = 0; i < input.length; i += 1) {
    const digit = lookup[input[i]];
    if (digit === undefined) {
      throw new Error(`Invalid base64 character "${input[i]}"`);
    }
    buffer = (buffer << 6) | digit;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes[index] = (buffer >> bits) & 0xff;
      index += 1;
      buffer &= (1 << bits) - 1;
    }
  }

  return bytes;
};

/**
 * base64url (RFC 4648 §5) of a byte array: standard base64 with `+/` swapped
 * for `-_` and the padding dropped. This is the encoding every part of a JWS
 * uses.
 */
export const base64url = (bytes) => encode(bytes, BASE64URL_ALPHABET, false);

/** The inverse of `base64url`, tolerating input that still carries padding. */
export const base64urlToBytes = (value) => decode(value, BASE64URL_LOOKUP);

/**
 * Standard base64, *padded* — what native code expects on the way in and hands
 * back on the way out (`Data(base64Encoded:)` on iOS, `Base64.decode` on
 * Android).
 */
export const bytesToBase64 = (bytes) => encode(bytes, BASE64_ALPHABET, true);

/** The inverse of `bytesToBase64`; padding is optional. */
export const base64ToBytes = (value) => decode(value, BASE64_LOOKUP);

/**
 * UTF-8 bytes of a string. `TextEncoder` is looked up per call rather than
 * held in a module constant because on React Native it is a polyfill installed
 * after this module has been imported; the hand-written encoder below is the
 * fallback for the moment before that (and for any engine without one).
 */
export const utf8Bytes = (value) => {
  const input = String(value);
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(input);
  }

  const bytes = [];
  for (let i = 0; i < input.length; i += 1) {
    let code = input.charCodeAt(i);

    if (code >= 0xd800 && code <= 0xdbff) {
      const next = input.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
        i += 1;
      } else {
        // A lone surrogate is not encodable; TextEncoder substitutes U+FFFD.
        code = 0xfffd;
      }
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      code = 0xfffd;
    }

    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      bytes.push(
        0xe0 | (code >> 12),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f)
      );
    } else {
      bytes.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f)
      );
    }
  }
  return Uint8Array.from(bytes);
};

const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:\/\//i;

/**
 * The `htu` claim is the request URL without query and fragment
 * (RFC 9449 §4.2). A plain string split on an absolute URL, deliberately not
 * `new URL(url)`: React Native's `URL` polyfill appends a `/` to the pathname
 * of a URL that has no query (`https://host/oidc/token` → `/oidc/token/`), and
 * the provider rejects that as an `htu` mismatch. Anything that is not an
 * absolute URL is kept as-is rather than dropped — the provider decides what
 * to make of it.
 */
export const stripUrlForHtu = (url) =>
  typeof url === 'string' && ABSOLUTE_URL.test(url)
    ? url.split(/[?#]/)[0]
    : url;

/**
 * RFC 7638 JWK thumbprint: base64url(sha256(<the required members, in
 * lexicographic order, no whitespace>)). For an EC key those members are
 * `crv`, `kty`, `x`, `y` — the object literal below is in that exact order,
 * which is the order `JSON.stringify` serialises it in.
 */
export const computeThumbprint = async ({ sha256, publicJwk }) => {
  const { crv, kty, x, y } = publicJwk;
  return base64url(await sha256(JSON.stringify({ crv, kty, x, y })));
};

const SIGNATURE_COMPONENT_LENGTH = 32;

/**
 * Convert an ECDSA signature from the DER sequence
 * `0x30 <len> 0x02 <rlen> <r> 0x02 <slen> <s>` to the raw `r||s` a JWS wants.
 * Mobile's native signing APIs return DER; WebCrypto already returns raw, so
 * only the mobile signer calls this.
 *
 * Only short-form lengths are accepted: a P-256 signature is at most 72 bytes,
 * so a length byte of 0x80 or above is malformed rather than long-form.
 */
export const derToRawEcdsaSignature = (der) => {
  const bytes = toBytes(der);

  const readLength = (offset) => {
    if (offset >= bytes.length) {
      throw new Error('Malformed DER signature: truncated');
    }
    const length = bytes[offset];
    if (length >= 0x80) {
      throw new Error('Malformed DER signature: long-form length');
    }
    return length;
  };

  if (bytes[0] !== 0x30) {
    throw new Error('Malformed DER signature: expected a SEQUENCE');
  }
  if (readLength(1) + 2 !== bytes.length) {
    throw new Error('Malformed DER signature: wrong sequence length');
  }

  const readInteger = (offset) => {
    if (bytes[offset] !== 0x02) {
      throw new Error('Malformed DER signature: expected an INTEGER');
    }
    const length = readLength(offset + 1);
    const start = offset + 2;
    const end = start + length;
    if (length === 0 || end > bytes.length) {
      throw new Error('Malformed DER signature: truncated INTEGER');
    }

    let value = bytes.subarray(start, end);
    // DER prefixes a 0x00 where the high bit would make the integer negative.
    while (value.length > 1 && value[0] === 0x00) {
      value = value.subarray(1);
    }
    if (value.length > SIGNATURE_COMPONENT_LENGTH) {
      throw new Error('Malformed DER signature: component is too long');
    }
    return { value, end };
  };

  const r = readInteger(2);
  const s = readInteger(r.end);
  if (s.end !== bytes.length) {
    throw new Error('Malformed DER signature: trailing bytes');
  }

  const raw = new Uint8Array(SIGNATURE_COMPONENT_LENGTH * 2);
  raw.set(r.value, SIGNATURE_COMPONENT_LENGTH - r.value.length);
  raw.set(s.value, raw.length - s.value.length);
  return raw;
};

const base64urlJson = (value) => base64url(utf8Bytes(JSON.stringify(value)));

/**
 * Build one proof for one request (RFC 9449 §4.2). `htu` is stripped of query
 * and fragment, `ath` is added only on resource requests (where an access
 * token is being presented) and `nonce` only when a server has asked for one.
 *
 * Every call mints a fresh `jti`, so a retried request never replays one.
 *
 * The crypto is the platform's: `sha256(string)` returns the digest bytes (or
 * a promise of them), `sign(Uint8Array)` resolves the raw 64-byte `r||s`
 * signature, and `randomBytes(count)` returns that many random bytes.
 */
export const createDpopProof = async ({
  publicJwk,
  htm,
  htu,
  accessToken,
  nonce,
  sha256,
  sign,
  randomBytes,
}) => {
  const header = { typ: 'dpop+jwt', alg: 'ES256', jwk: publicJwk };

  const claims = {
    jti: base64url(randomBytes(16)),
    htm,
    htu: stripUrlForHtu(htu),
    iat: Math.floor(Date.now() / 1000),
  };

  if (accessToken) {
    claims.ath = base64url(await sha256(accessToken));
  }
  if (nonce) {
    claims.nonce = nonce;
  }

  const signingInput = `${base64urlJson(header)}.${base64urlJson(claims)}`;
  const signature = toBytes(await sign(utf8Bytes(signingInput)));

  if (signature.length !== SIGNATURE_COMPONENT_LENGTH * 2) {
    throw new Error(
      `Expected a 64-byte raw ES256 signature, got ${signature.length} bytes`
    );
  }

  return `${signingInput}.${base64url(signature)}`;
};
